import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BoringAvatar from "boring-avatars";
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  AGENT_BRAND_COLORS,
  Agent,
  AgentScope,
  agentListPath,
  deleteAgent,
} from "~/services/agents";
import { DeleteAgentDialog } from "./DeleteAgentDialog";
import { StatusBadge } from "./StatusBadge";

interface AgentCardProps {
  agent: Agent;
  canManage: boolean;
}

function formatUpdatedAt(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const AgentCard = ({ agent, canManage }: AgentCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const duplicateAgentAction = useDuplicateAgent();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDuplicate = () => duplicateAgentAction(agent.id, agent.display_name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutate(agentListPath());
      showSuccess(`"${agent.display_name}" deleted`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <>
      <Card
        className={cn(
          "group h-full gap-0 overflow-hidden p-0",
          "cursor-pointer transition-colors hover:border-border/80 hover:bg-accent/40"
        )}
        onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
      >
        <div className="flex items-center gap-3 bg-[var(--agent-card-header-bg)] px-5 py-4">
          <div className="size-11 shrink-0 overflow-hidden rounded-full ring-1 ring-border">
            <BoringAvatar size={44} name={agent.name} variant="beam" colors={AGENT_BRAND_COLORS} />
          </div>
          <div className="min-w-0 flex-1">
            <span className="block text-base font-semibold leading-tight text-foreground">
              {agent.display_name}
            </span>
          </div>
          {canManage && (
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "size-7 shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground",
                      "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
                      "focus-visible:ring-0 focus-visible:border-transparent"
                    )}
                    aria-label={`Actions for ${agent.display_name}`}
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleDuplicate}>
                    <Copy />
                    Duplicate
                  </DropdownMenuItem>
                  {agent.scope !== AgentScope.SYSTEM && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                        <Trash2 />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3 px-5 pb-3 pt-3">
          <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{agent.description}</p>

          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusBadge status={agent.status} />
              {agent.scope === AgentScope.SYSTEM && (
                <Badge variant="outline" className="rounded-full">
                  System
                </Badge>
              )}
            </div>
            <p className="shrink-0 text-xs text-muted-foreground/60">
              Updated {formatUpdatedAt(agent.updated_at)}
            </p>
          </div>
        </div>
      </Card>

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
